import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, query, state, queryAll } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_POLICY_PANEL_ACCORDION_TEMPLATE } from '../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../utils/translator.helper';
import { RootState, store } from '@nirvana/redux/store';
import { TextHighlightMixin } from '../../../../../../mixins';
import { connect } from 'pwa-helpers';
import { classMap } from 'lit/directives/class-map.js';
import { FormGroup } from '../../../../../../ui/gzForm/formGroup/form-group';
import { PolicyAccordionFormValue, PolicySearchableKeywords } from '../../../../../endpoints/policyPanel/utils';
import { selectPoliciesState } from '../../../../../endpoints/policyPanel/redux/slice';
import {
  getIsPolicyDataLoaded,
  getModuleState,
  getSectionMasks,
  getSectionValidity,
  setSectionValidity,
} from '../../../../../endpoints/policyPanel/redux/actions';
import { PolicyPanelManager } from '../../../../../endpoints/policyPanel/PolicyPanelManager';
import { BehaviorSubject, Observable, skip, Subscription } from 'rxjs';
import { FormGroupValue } from '../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { USE_EXT_JS_VERSION } from '../../../../../endpoints/policyPanel/config/config';
import { PolicyPanelEvents } from '../../../../../endpoints/policyPanel/utils/events';
import { CommonEvents } from '@nirvana/shared/constants';
import { getSupportLinkForPlatform } from '../../../../../endpoints/policyPanel/utils/links';
import { PoliciesApiClientsType } from '../../dependencies/PolicyDependenciesContainer';
import { PolicyAppState, SectionMask, SectionStatusType } from '../../../../../endpoints/policyPanel/redux/types';
import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { SectionId, TreePath } from '@nirvana/shared/dtos/types/policies/common';
import { areArraysEqual } from '@nirvana/shared/basic-types/object';
import { GzInfoBoxWebComponent } from '../../../../../../ui/gzInfoBox';
import { getWarningMessageByMask } from '../../../../../endpoints/policyPanel/searchableComponents/searchablePolicyPanel/utils/treeItems';
import { InfoBoxSeverity } from '../../../../../../ui/gzInfoBox/types';
import { setPathToBeUpdatedWithStatus } from '../../../../../endpoints/policyPanel/redux/statusSlice/actions.status';
import { FormEvent } from '../../../../../../ui/gzForm/formField/types/form-event.enum';

@customElement(WEB_COMPONENT_POLICY_PANEL_ACCORDION_TEMPLATE)
export abstract class GzPolicyPanelAccordionTemplateWebComponent<
  T extends PolicyAccordionFormValue,
  TKeywords extends PolicySearchableKeywords,
  TClients extends PoliciesApiClientsType = PoliciesApiClientsType
> extends TextHighlightMixin(connect(store)(LitElement)) {
  /**
   * This internal reactive property helps re-render this accordion on change.
   * When a value is set, the loading state of accordion will be changed with the actual form group.
   */
  @state()
  protected valueT: T | null = null;

  /**
   * This property is used to store masks for the accordion.
   * @protected
   */
  @state()
  protected masks: SectionMask[] = [];

  /**
   * This property is used to store additional info for the accordion.
   */
  protected additionalInfo;

  /***
   * This property is used to store api clients for the accordion.
   */
  protected clients: TClients;

  /**
   * Path for knowing witch section in tree is displayed using this template. (moduleId + sectionId)
   */
  protected abstract path: TreePath;

  /**
   * Searchable keywords for section.
   */
  protected abstract searchableKeywords: { [type in TKeywords]: SearchableKeyword };

  /**
   * Form group for current accordion.
   */
  protected formGroup!: FormGroup;
  /**
   * Subscriptions for current accordion.
   */
  protected subscriptions: Subscription[] = [];
  /**
   *  Boolean used to know if the current component is ready (form group is loaded and value is set).
   */
  protected isReady = false;
  /**
   *  This property is used only if child component has to wait for validation to finish before unsubscribing
   */
  protected hasPendingValidationSubject: BehaviorSubject<boolean>;
  protected hasPendingValidation$: Observable<boolean>;

  // mask fields
  @query('[title-mask-element]')
  private titleMaskElement: HTMLElement;
  @query('[info-box-mask-element]')
  private infoBoxMaskElement: GzInfoBoxWebComponent;
  @query('[content-mask-element]')
  private contentMaskElement: HTMLElement;
  // status fields
  @queryAll('[status-field]')
  private statusFields: HTMLElement[];

  /**
   * Template styles.
   */
  static get styles() {
    return styles;
  }

  /**
   * When store state changed and data is loaded for current path, value needs to be received.
   * @param rootState
   */
  public async stateChanged(rootState: RootState) {
    const appState = selectPoliciesState(rootState);
    const masks = getSectionMasks(appState, this.path);

    if (!areArraysEqual([...this.masks], [...masks])) {
      this.masks = masks;
      this.setMask(this.masks);
    }

    if (this.valueT) {
      // data already loaded
      return;
    }

    if (!this.shouldBeRendered(rootState)) {
      return;
    }

    // set value, which will trigger component re-render
    this.valueT = this.getValueFromManager();
    this.additionalInfo = this.getAdditionalInfoFromManager();
    if (this.formGroup) {
      this.setFormData();
      await this.updateComplete;
      this.setValidators();
      this.formGroup.validate(true);
      this.isReady = true;
    }
  }

  /**
   * Create form group after the first update.
   * @param _changedProperties
   */
  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    // wait for update to complete before creating form group
    this.updateComplete.then(async () => {
      this.createFormGroup();
      this.setMask(this.masks);
      this.setClients();
      this.subscribeToValidityEvent();
      this.subscribeToStatusFieldEvents();
      if (this.valueT) {
        this.setFormData();
        await this.updateComplete;
        this.setValidators();
        this.formGroup.validate(true);
        this.isReady = true;
      }
    });
  }

  /**
   * Render component template:
   * - common: loading container,link and buttons
   * - specific: title and content
   */
  protected render() {
    const isLoadingClass = classMap({
      'is-loading': !this.valueT,
    });

    const policyPanelVersion = classMap({
      'use-ext-js-version': USE_EXT_JS_VERSION,
    });

    return html`
      <div class="wrapper ${isLoadingClass}">
        <div class="navigation-header">
          <gz-policy-panel-accordion-navigation-header
            .moduleId="${this.path.moduleId}"
            .sectionId="${this.path.sectionId}"
          ></gz-policy-panel-accordion-navigation-header>
        </div>

        <div class="title-header">
          <div class="module-name" title-mask-element>${this.renderTitle()}</div>
          <div class="module-link">
            ${this.renderContentNextToSupportLink()}
            <gz-link
              class="support-link"
              iconsize="20"
              type="underline"
              color="secondary"
              href="${getSupportLinkForPlatform(this.path, ['support'])}"
              fontsize="12"
            >
              <span>${translateFn('Get help from Support Center')}</span>
            </gz-link>
          </div>
        </div>

        <div class="loading-container">
          <gz-progress-spinner size="large"></gz-progress-spinner>
        </div>

        <div class="content">
          ${this.renderInfoText()}

          <gz-info-box severity="${InfoBoxSeverity.WARNING}" class="mt-2 hidden" info-box-mask-element> </gz-info-box>

          <div content-mask-element>${this.renderContent()}</div>
        </div>
      </div>

      <gz-policy-panel-accordion-buttons
        class="${policyPanelVersion}"
        @policyPanelPreSave="${this.handleSaveButtonClicked}"
      ></gz-policy-panel-accordion-buttons>
    `;
  }

  /**
   * Render content next to support link.
   * @protected
   */
  protected renderContentNextToSupportLink(): TemplateResult {
    return html``;
  }

  /**
   * Check if section should be rendered ( it is available and it has data loaded).
   * @param rootState
   */
  private shouldBeRendered(rootState?: RootState): boolean {
    if (!rootState) {
      rootState = store.getState();
    }
    const policiesApp = selectPoliciesState(rootState);

    return this.isAvailable(policiesApp) && this.hasDataLoaded(policiesApp);
  }

  /**
   * Check if section is available (it is in the tree).
   * @param policiesApp
   */
  private isAvailable(policiesApp: PolicyAppState): boolean {
    // check section is loaded (otherwise this section is not displayed)
    const moduleState = getModuleState(policiesApp, this.path.moduleId);

    return !!moduleState && !!moduleState.sections[this.path.sectionId];
  }

  /**
   * Check if data is loaded for current path.
   * @param policiesApp
   */
  private hasDataLoaded(policiesApp: PolicyAppState): boolean {
    // check data is loaded
    const isPolicyDataLoaded = getIsPolicyDataLoaded(policiesApp);
    const isAdditionalInfoLoaded = this.isAdditionalInfoLoaded(policiesApp);

    // both policy data and additional info must be loaded
    return isPolicyDataLoaded && isAdditionalInfoLoaded;
  }

  /**
   * Subscribe to status field events.
   */
  private subscribeToStatusFieldEvents(): void {
    if (USE_EXT_JS_VERSION) {
      return;
    }

    this.statusFields.forEach(field => {
      field.addEventListener(FormEvent.ValueChange, this.onStatusFieldChange.bind(this));
    });
  }

  /**
   * On status field change, update section status in redux store.
   */
  private async onStatusFieldChange() {
    // wait for update to complete
    await this.updateComplete;

    // update section status
    PolicyPanelManager.getInstance().updateSectionStatus(this.path, this.getFormGroupValue());
  }

  /**
   * Set validation rules for form fields. Override this method in child classes to set custom validation rules.
   */
  protected setValidators(): void {}

  /**
   * Subscribe to validity event received from form group.
   */
  protected subscribeToValidityEvent(): void {
    // subscribe to form group validity changes (skip first value, because it is always true)
    const formGroupValidSubscription = this.formGroup.valid$.pipe(skip(1)).subscribe({
      next: valid => {
        this.changeValidityIfNecessary(valid);
      },
    });

    this.subscriptions.push(formGroupValidSubscription);
  }

  /**
   * Change section validity in redux store if different from previous one.
   * @param valid the current validity value of form group
   */
  protected changeValidityIfNecessary(valid: boolean): void {
    if (!this.shouldBeRendered()) {
      return;
    }

    const isValid = getSectionValidity(store.getState().policiesApp, this.path);

    // dispatch only when different
    if (isValid !== valid) {
      store.dispatch(setSectionValidity({ path: this.path, value: valid }));

      if (!valid) {
        store.dispatch(
          setPathToBeUpdatedWithStatus({
            path: this.path,
            status: { type: SectionStatusType.WARNING },
          })
        );
      } else {
        this.onStatusFieldChange();
      }

      if (USE_EXT_JS_VERSION) {
        // dispatch event for ExtJs compatibility
        this.dispatchEvent(
          new CustomEvent(CommonEvents.VALIDITY_CHANGE, {
            bubbles: true,
            composed: true,
          })
        );
      }
    }
  }

  /**
   * Save Button Click handler, send current form value to manager.
   */
  private handleSaveButtonClicked(): void {
    this.unsubscribe();
    this.sendFormValueToManager();

    // dispatch event to Searchable component for sending data to backend
    this.dispatchEvent(
      new CustomEvent(PolicyPanelEvents.SAVE, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * When this accordion is connected, track page view.
   */
  public connectedCallback() {
    super.connectedCallback();

    // track page view
    analytics.trackPageView({
      parentName: 'policy',
      name: this.path.moduleId + '-' + (this.path.sectionId === SectionId.NONE ? 'general' : this.path.sectionId),
    });
  }

  /**
   * When this accordion is destroyed, form value has to be sent to manager.
   */
  public disconnectedCallback(): void {
    if (this.hasPendingValidationSubject && this.hasPendingValidationSubject.value) {
      // if there is a pending validation, wait for it to finish before unsubscribing
      this.hasPendingValidation$.subscribe({
        next: async (value: boolean) => {
          if (!value) {
            this.unsubscribe();
          }
        },
      });
    } else {
      // no pending validation, unsubscribe immediately
      this.unsubscribe();
    }

    this.sendFormValueToManager();
    super.disconnectedCallback();
  }

  /**
   * Send form value to manager if it has changes and mark it as changed.
   */
  protected sendFormValueToManager() {
    const policyAppState: PolicyAppState = selectPoliciesState(store.getState());
    if (!this.hasDataLoaded(policyAppState)) {
      return;
    }

    const value = this.getFormGroupValue();
    if (!value) {
      return;
    }

    PolicyPanelManager.getInstance().setPartialPolicy(value);
    this.sendAdditionalInfoToManagerIfNeeded();
  }

  /**
   * Check if additional info is loaded. This method must be overridden in child classes if needed.
   */
  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return true;
  }

  /**
   * Get additional info from manager if needed.
   */
  protected getAdditionalInfoFromManager(): any {
    return null;
  }

  /**
   * Send additional information to manager if needed.
   */
  protected sendAdditionalInfoToManagerIfNeeded(): void {
    return;
  }

  /**
   * Set api clients if needed.
   */
  protected setClients(): void {
    return;
  }

  /**
   * Set mask for title and content. Show warning message.
   * @param masks
   */
  protected setMask(masks: SectionMask[]): void {
    if (!masks.length) {
      return;
    }

    if (!this.titleMaskElement || !this.contentMaskElement || !this.infoBoxMaskElement) {
      return;
    }

    this.titleMaskElement.classList.add('disabled');
    this.contentMaskElement.classList.add('disabled');

    this.infoBoxMaskElement.classList.remove('hidden');
    this.infoBoxMaskElement.innerHTML = getWarningMessageByMask(this.path, this.masks[0]);
  }

  /**
   * Unsubscribe.
   */
  private unsubscribe(): void {
    // unsubscribe from all subscriptions
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    // remove event listeners
    this.statusFields.forEach(field => {
      field.removeEventListener(FormEvent.ValueChange, this.onStatusFieldChange.bind(this));
    });

    this.subscriptions = [];
    this.formGroup?.unsubscribe();
  }

  /**
   * ExtJs useful method.
   * Get form value.
   */
  public get value(): string {
    if (!this.valueT) {
      // no data loaded
      return '';
    }

    const formValue = this.getFormGroupValue();
    return JSON.stringify(formValue);
  }

  /**
   * ExtJs useful method.
   * Get validity section.
   */
  public isValid(): boolean {
    if (!this.valueT) {
      // no data loaded, mark it as valid
      return true;
    }

    return getSectionValidity(store.getState().policiesApp, this.path);
  }

  /**
   * ExtJs useful method.
   * Reset value and set form data.
   */
  public reset() {
    this.valueT = null;
  }

  protected abstract renderTitle(): void;
  protected abstract renderInfoText(): void;
  protected abstract renderContent(): void;

  protected abstract getValueFromManager(): T;
  protected abstract createFormGroup(): void;
  protected abstract setFormData(): void;
  protected abstract getFormGroupValue(): FormGroupValue | null;
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_UI_SETTINGS_COMPONENT_TEMPLATE: GzPolicyPanelAccordionTemplateWebComponent<
      PolicyAccordionFormValue,
      PolicySearchableKeywords
    >;
  }
}
